// "Queue for Defra": pre-flight for the selected receipts.
//
// Shows, per receipt, whether it can be queued and, if not, every reason in plain
// staff language with what to fix. Receipts that are not eligible are NEVER sent
// to the queue endpoint (the server re-checks as well). Queueing only puts the
// receipts in the worker's queue; nothing is sent to Defra from here.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Clock, XCircle } from 'lucide-react';
import { Modal } from '../../../components/ui/Modal/Modal';
import { Button } from '../../../components/ui/Button/Button';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { preflightReceipts, queueReceipts, type DwtPreflightRow, type DwtQueueRow } from '../../../api/dwt';
import styles from '../../dwt/dwt.module.css';

export interface QueueForDefraDialogProps {
  open: boolean;
  /** Selected receipts: enc_id plus the number to show. */
  receipts: Array<{ enc_id: string; unique_id: string }>;
  onClose: () => void;
  /** Called after a queue attempt so the list can refresh. */
  onQueued: () => void;
}

const STATE_TEXT: Record<DwtPreflightRow['state'], string> = {
  will_queue: 'Ready to queue',
  already_queued: 'Already queued',
  already_retrying: 'Already waiting to be retried',
  ineligible: 'Cannot be queued yet',
  not_found: 'Receipt not found',
};

const RESULT_TEXT: Record<DwtQueueRow['result'], string> = {
  queued: 'Queued',
  already_queued: 'Already queued',
  already_retrying: 'Already waiting to be retried',
  ineligible: 'Not queued: needs fixing',
  not_found: 'Not found',
};

export function QueueForDefraDialog({ open, receipts, onClose, onQueued }: QueueForDefraDialogProps): JSX.Element | null {
  const ids = receipts.map((r) => r.enc_id);
  const nameById = new Map(receipts.map((r) => [r.enc_id, r.unique_id]));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DwtQueueRow[] | null>(null);

  const pre = useQuery({
    queryKey: ['dwt', 'preflight', ids],
    queryFn: () => preflightReceipts(ids),
    enabled: open && ids.length > 0 && result === null,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  const rows = pre.data ?? [];
  const queueable = rows.filter((r) => r.state === 'will_queue');
  const blocked = rows.filter((r) => r.state === 'ineligible' || r.state === 'not_found');

  function close() {
    if (busy) return;
    setResult(null);
    setError(null);
    onClose();
  }

  async function queueEligible() {
    setBusy(true);
    setError(null);
    try {
      // Only receipts the pre-flight said can be queued are ever sent.
      const out = await queueReceipts(queueable.map((r) => r.receipt_enc_id));
      setResult(out);
      onQueued();
    } catch {
      setError('Could not queue the receipts. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  const queuedCount = result?.filter((r) => r.result === 'queued').length ?? 0;

  return (
    <Modal
      open={open}
      onClose={close}
      title="Queue for Defra"
      size="lg"
      preventClose={busy}
      footer={
        result === null ? (
          <>
            <Button variant="secondary" onClick={close} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={() => void queueEligible()} loading={busy} disabled={queueable.length === 0 || pre.isLoading}>
              {queueable.length === 0
                ? 'Nothing to queue'
                : `Queue ${queueable.length} ${queueable.length === 1 ? 'receipt' : 'receipts'}`}
            </Button>
          </>
        ) : (
          <Button onClick={close}>Close</Button>
        )
      }
    >
      <div className={styles.stack}>
        {pre.isLoading ? (
          <div className={styles.row}>
            <Spinner /> <span>Checking {ids.length} {ids.length === 1 ? 'receipt' : 'receipts'}…</span>
          </div>
        ) : pre.isError ? (
          <p role="alert">Could not check the receipts. Please try again.</p>
        ) : result !== null ? (
          <>
            <p role="status">
              <strong>
                {queuedCount} {queuedCount === 1 ? 'receipt' : 'receipts'} queued.
              </strong>{' '}
              They are sent to Defra by the background worker; nothing was sent just now.
            </p>
            <table className={styles.table} aria-label="Queue result">
              <tbody>
                {result.map((r) => (
                  <tr key={r.receipt_enc_id}>
                    <td>{nameById.get(r.receipt_enc_id) ?? 'Receipt'}</td>
                    <td>{RESULT_TEXT[r.result]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : (
          <>
            <p role="status">
              <strong>
                {queueable.length} of {rows.length}
              </strong>{' '}
              can be queued.
              {blocked.length > 0 ? ' The others are listed below with what to fix; they will not be queued.' : ''}
            </p>
            <table className={styles.table} aria-label="Pre-flight check">
              <thead>
                <tr>
                  <th>Receipt</th>
                  <th>Result</th>
                  <th>What to fix</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.receipt_enc_id} data-state={r.state}>
                    <td>{r.unique_id ?? nameById.get(r.receipt_enc_id) ?? 'Receipt'}</td>
                    <td>
                      <span className={styles.row}>
                        {r.state === 'will_queue' ? (
                          <CheckCircle2 size={16} aria-hidden />
                        ) : r.state === 'ineligible' || r.state === 'not_found' ? (
                          <XCircle size={16} aria-hidden />
                        ) : (
                          <Clock size={16} aria-hidden />
                        )}
                        {STATE_TEXT[r.state]}
                      </span>
                    </td>
                    <td>
                      {r.reasons.length === 0 ? (
                        <span className={styles.muted}>—</span>
                      ) : (
                        r.reasons.map((reason) => (
                          <div key={reason.code} className={styles.reason}>
                            <span>{reason.message}</span>
                            <span className={styles.fix}>{reason.fix}</span>
                          </div>
                        ))
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        {error ? <p role="alert">{error}</p> : null}
      </div>
    </Modal>
  );
}
